import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  computed,
  ElementRef,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { ShellStateService } from '@layout/state/shell-state.service';
import { AuthFacadeService } from '@features/auth/services/auth-facade.service';
import { MyAccountFacadeService } from '@features/my-account/services/my-account-facade.service';

/** Folga entre sair do bloco do menu e fechá-lo, curta o bastante para não ser notada. */
const USER_MENU_CLOSE_DELAY_MS = 180;

@Component({
  selector: 'app-dashboard-header',
  standalone: false,
  templateUrl: './dashboard-header.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class]':
      "actionsOnly() ? 'block' : 'block mb-7.5 sticky top-0 z-100 min-w-0 bg-[#fafafa] max-mobile:mb-4 [@media(max-height:500px)]:static'",
  },
})
export class DashboardHeaderComponent implements OnInit, OnDestroy {
  private readonly shellState = inject(ShellStateService);
  private readonly auth = inject(AuthFacadeService);
  private readonly myAccount = inject(MyAccountFacadeService);
  private readonly router = inject(Router);

  /**
   * Below 48rem the layout's top bar renders a second header with only the bell, avatar
   * and menu, beside the drawer button; the page header then keeps the title block.
   */
  readonly actionsOnly = input(false);

  /** Mobile only: the screen phrase is out of the flow until the info button opens it. */
  readonly phraseOpen = signal(false);

  /** Dropdown wrapper of the avatar menu — used to tell whether focus left the block. */
  private readonly userMenu = viewChild<ElementRef<HTMLElement>>('userMenu');

  /** Avatar dropdown: opens on hover (and on focus, where there is no hover). */
  readonly userMenuOpen = signal(false);
  private userMenuCloseTimer: ReturnType<typeof setTimeout> | null = null;

  readonly title = this.shellState.title;
  readonly phrase = this.shellState.phrase;
  readonly phraseParams = this.shellState.phraseParams;
  readonly lastUpdate = this.shellState.lastUpdate;

  /** Authenticated user (hydrated from localStorage by AuthStateService). */
  readonly user = computed(() => this.auth.vm().user);
  /** First letter of the user's name for the avatar badge. */
  readonly userInitial = computed(() => this.user()?.name?.charAt(0) ?? '');

  /** Access role of the authenticated user, lowercased so casing never matters. */
  private readonly role = computed(() => this.user()?.role?.toLowerCase() ?? null);

  /**
   * Organization settings moved to the avatar menu; same opt-in hiding as the old
   * sidebar entry (audit/users): hidden from analyst, manager and coordinator.
   */
  readonly showOrganizationSettings = computed(() => {
    const role = this.role();
    if (role === null) {
      return true;
    }

    return !['analyst', 'manager', 'coordinator'].includes(role);
  });

  /**
   * Profile picture as the object URL downloaded by the facade, or `null` while
   * there is none — the badge falls back to the initial. `avatar.url` cannot be
   * used here: the bucket is private and `<img>` sends no bearer token.
   */
  readonly avatarUrl = computed(() => this.myAccount.vm().avatarUrl);

  ngOnInit(): void {
    // The header lives on every dashboard route, so it is what warms the profile
    // (and with it the picture); the account page reloads it on its own.
    this.myAccount.ensureProfile();
  }

  ngOnDestroy(): void {
    this.cancelUserMenuClose();
  }

  openUserMenu(): void {
    this.cancelUserMenuClose();
    this.userMenuOpen.set(true);
  }

  closeUserMenu(): void {
    this.cancelUserMenuClose();
    this.userMenuOpen.set(false);
  }

  /**
   * Fecha depois de uma folga. O gatilho tem 40px e o painel se abre para a
   * esquerda, então o trajeto diagonal do ponteiro passa por fora do bloco antes
   * de alcançar o painel - fechar no mesmo instante tornaria o menu inalcançável.
   */
  scheduleUserMenuClose(): void {
    this.cancelUserMenuClose();
    this.userMenuCloseTimer = setTimeout(() => {
      this.userMenuCloseTimer = null;
      this.userMenuOpen.set(false);
    }, USER_MENU_CLOSE_DELAY_MS);
  }

  private cancelUserMenuClose(): void {
    if (this.userMenuCloseTimer !== null) {
      clearTimeout(this.userMenuCloseTimer);
      this.userMenuCloseTimer = null;
    }
  }

  /**
   * Só o mouse abre no hover. No toque, o navegador emula `mouseenter`/foco antes do
   * clique, e o clique logo em seguida fechava o menu que acabara de abrir - no mobile
   * ele é a única entrada para a conta, então toque e teclado passam só pelo clique.
   */
  onUserMenuPointerEnter(event: PointerEvent): void {
    if (event.pointerType === 'mouse') this.openUserMenu();
  }

  onUserMenuPointerLeave(event: PointerEvent): void {
    if (event.pointerType === 'mouse') this.scheduleUserMenuClose();
  }

  /** Toque e teclado não têm hover: o clique no gatilho continua abrindo e fechando. */
  toggleUserMenu(): void {
    this.userMenuOpen.update((open) => !open);
  }

  /**
   * Fecha quando o foco sai do bloco. O `mouseleave` sozinho não cobre o teclado,
   * que abre o menu pelo foco no gatilho e sairia dele sem nunca fechá-lo.
   */
  onUserMenuFocusOut(event: FocusEvent): void {
    const wrapper = this.userMenu()?.nativeElement;
    const next = event.relatedTarget;
    if (wrapper && (!(next instanceof Node) || !wrapper.contains(next))) {
      this.closeUserMenu();
    }
  }

  togglePhrase(): void {
    this.phraseOpen.update((open) => !open);
  }

  /** The bell opens the alert center. */
  openAlertCenter(): void {
    this.router.navigate(['/dashboard/alert-center']);
  }

  /** Navigates to the account page and closes the dropdown, which the shell keeps mounted. */
  openMyAccount(): void {
    this.closeUserMenu();
    this.router.navigate(['/dashboard/my-account']);
  }

  /** Navigates to organization settings and closes the dropdown. */
  openOrganizationSettings(): void {
    this.closeUserMenu();
    this.router.navigate(['/dashboard/organization-settings']);
  }

  /** Revokes the session, clears local auth state and returns to the login screen. */
  logout(): void {
    const toLogin = (): void => {
      this.router.navigate(['/auth']);
    };
    // `complete` covers both the API success and the no-token (EMPTY) path.
    this.auth.logout().subscribe({ complete: toLogin, error: toLogin });
  }

  /**
   * Trilha definida pela página (ex.: VCR > Nome > Documentos). Uma trilha com um
   * único item só repetiria o título da página, então nesse caso nada é exibido -
   * é o que acontece nas telas de primeiro nível, incluindo a própria Dashboard.
   */
  readonly breadcrumb = computed(() => {
    const custom = this.shellState.breadcrumb();
    return custom && custom.length > 1 ? custom : [];
  });
}
